import { useState } from 'react';
import {
  AppBar,
  Toolbar,
  Box,
  InputBase,
  IconButton,
  Button,
  Menu,
  MenuItem,
  Typography,
  Stack,
  useMediaQuery,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import AccountCircleIcon from '@mui/icons-material/AccountCircle';
import SearchIcon from '@mui/icons-material/Search';
import { Link as RouterLink, useNavigate, useLocation } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';

import { useAuthStore } from '../../stores/authStore';
import { authApi } from '../../services/authApi';
import { searchListings, type SearchResponse } from '../../services/searchApi';
import { Logo } from '../brand/Logo';

const NAV_LINKS: Array<{
  key: string;
  label: string;
  to: string;
  match: (path: string) => boolean;
}> = [
  { key: 'search', label: 'Search', to: '/', match: (p) => p === '/' },
  { key: 'browse', label: 'Browse', to: '/search', match: (p) => p.startsWith('/search') },
  {
    key: 'alerts',
    label: 'Alerts',
    to: '/dashboard/alerts',
    match: (p) => p.startsWith('/dashboard/alerts'),
  },
  // Temporarily hidden until we have enough traffic to pitch advertisers (#103).
  // The route + advertiser feature stay; we just don't surface the nav entry.
  // {
  //   key: 'advertise',
  //   label: 'Advertise',
  //   to: '/register-advertiser',
  //   match: (p) => p.startsWith('/register-advertiser') || p.startsWith('/dashboard/advertiser'),
  // },
];

/**
 * Top-of-page header ("tactical" nav).
 *
 * - Brand mark + wordmark returns to home.
 * - Primary nav links (real routes only).
 * - A live in-stock-count status pill (real number from a `limit=0` search).
 * - A compact site search that submits to /search?q=…
 * - Account: signed-out shows "Sign in" + a "Start a watch" CTA; signed-in
 *   shows an account menu (Dashboard / Sign out).
 */
export function Header() {
  const theme = useTheme();
  const t = theme.palette.tactical;
  const navigate = useNavigate();
  const location = useLocation();
  const isAuthenticated = useAuthStore((s) => s.isAuthenticated);
  const isAdmin = useAuthStore((s) => s.isAdmin);
  const signOut = useAuthStore((s) => s.signOut);
  const [query, setQuery] = useState('');
  const [menuAnchor, setMenuAnchor] = useState<null | HTMLElement>(null);
  const menuOpen = Boolean(menuAnchor);
  const showNav = useMediaQuery(theme.breakpoints.up('md'));
  const showStatus = useMediaQuery(theme.breakpoints.up('lg'));

  // Real, lightweight live count: total in-stock listings (no hit payloads).
  const liveCount = useQuery<SearchResponse>({
    queryKey: ['search', 'live-count'],
    queryFn: () => searchListings({ inStockOnly: true, limit: 0 }),
    staleTime: 60_000,
    retry: false,
  });

  function onSubmit(event: React.FormEvent<HTMLFormElement>) {
    event.preventDefault();
    const trimmed = query.trim();
    if (trimmed.length === 0) return;
    navigate(`/search?q=${encodeURIComponent(trimmed)}`);
  }

  async function onSignOut() {
    setMenuAnchor(null);
    try {
      await authApi.logout();
    } catch {
      /* logout is best-effort; we still drop local tokens. */
    }
    signOut();
    navigate('/');
  }

  return (
    <AppBar
      position="sticky"
      elevation={0}
      component="header"
      sx={{
        top: 0,
        bgcolor: `${t.bg}f0`,
        backdropFilter: 'blur(8px)',
        borderBottom: `1px solid ${t.line}`,
        color: t.text,
      }}
    >
      <Toolbar sx={{ gap: 2, minHeight: { xs: 60, md: 64 } }}>
        <Box
          component={RouterLink}
          to="/"
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 1.25,
            textDecoration: 'none',
            color: 'inherit',
            flexShrink: 0,
          }}
          aria-label="Round Recon home"
        >
          <Logo size={22} />
          <Typography
            component="span"
            sx={{ fontWeight: 600, fontSize: '0.95rem', letterSpacing: '-0.01em' }}
          >
            Round Recon
          </Typography>
        </Box>

        {showNav && (
          <Stack
            component="nav"
            aria-label="Primary"
            direction="row"
            spacing={2.5}
            sx={{ ml: 1.5 }}
          >
            {NAV_LINKS.map((link) => {
              const active = link.match(location.pathname);
              return (
                <Box
                  key={link.key}
                  component={RouterLink}
                  to={link.to}
                  aria-current={active ? 'page' : undefined}
                  sx={{
                    fontSize: '0.8125rem',
                    textDecoration: 'none',
                    color: active ? t.text : t.dim,
                    '&:hover': { color: t.text },
                  }}
                >
                  {link.label}
                </Box>
              );
            })}
          </Stack>
        )}

        <Box sx={{ flex: 1 }} />

        <Box
          component="form"
          role="search"
          aria-label="Site search"
          onSubmit={onSubmit}
          sx={{
            display: { xs: 'none', sm: 'flex' },
            alignItems: 'center',
            gap: 1,
            bgcolor: t.panel,
            border: `1px solid ${t.line}`,
            borderRadius: 2,
            px: 1.25,
            height: 38,
            width: { sm: 200, md: 260 },
            transition: 'border-color 120ms, box-shadow 120ms',
            // #104: surface a clear focus indicator on the whole field — the
            // input alone only shows a caret.
            '&:focus-within': {
              borderColor: t.accent,
              boxShadow: `0 0 0 2px ${t.accent}55`,
            },
          }}
        >
          <SearchIcon sx={{ fontSize: 18, color: t.dim }} aria-hidden="true" />
          <InputBase
            type="search"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Search ammo…"
            inputProps={{ 'aria-label': 'Search ammunition by caliber, brand, or use case' }}
            sx={{ flex: 1, color: t.text, fontSize: '0.8125rem' }}
          />
          <IconButton type="submit" size="small" aria-label="Search" sx={{ color: t.dim }}>
            <SearchIcon sx={{ fontSize: 18 }} />
          </IconButton>
        </Box>

        {showStatus && typeof liveCount.data?.totalResults === 'number' && (
          <Stack
            direction="row"
            spacing={1}
            alignItems="center"
            sx={{ fontFamily: t.monoFont, fontSize: 11, color: t.dim, flexShrink: 0 }}
          >
            <Box
              sx={{
                width: 6,
                height: 6,
                borderRadius: '50%',
                bgcolor: t.accent,
                boxShadow: `0 0 10px ${t.accent}`,
              }}
            />
            <span>{liveCount.data.totalResults.toLocaleString()} in stock</span>
          </Stack>
        )}

        {isAuthenticated ? (
          <>
            <IconButton
              aria-label="Open account menu"
              aria-haspopup="menu"
              aria-expanded={menuOpen}
              aria-controls={menuOpen ? 'account-menu' : undefined}
              onClick={(event) => setMenuAnchor(event.currentTarget)}
              sx={{ color: 'inherit' }}
            >
              <AccountCircleIcon />
            </IconButton>
            <Menu
              id="account-menu"
              anchorEl={menuAnchor}
              open={menuOpen}
              onClose={() => setMenuAnchor(null)}
              MenuListProps={{ 'aria-label': 'Account actions' }}
            >
              <MenuItem component={RouterLink} to="/dashboard" onClick={() => setMenuAnchor(null)}>
                Dashboard
              </MenuItem>
              <MenuItem
                component={RouterLink}
                to="/dashboard/watchlist"
                onClick={() => setMenuAnchor(null)}
              >
                Watchlist
              </MenuItem>
              {isAdmin && (
                <MenuItem component={RouterLink} to="/admin" onClick={() => setMenuAnchor(null)}>
                  Admin
                </MenuItem>
              )}
              <MenuItem onClick={onSignOut}>Sign out</MenuItem>
            </Menu>
          </>
        ) : (
          <Stack direction="row" spacing={1} sx={{ flexShrink: 0 }}>
            <Button component={RouterLink} to="/login" variant="outlined" size="small">
              Sign in
            </Button>
            <Button
              component={RouterLink}
              to="/register"
              variant="contained"
              color="primary"
              size="small"
              sx={{ display: { xs: 'none', sm: 'inline-flex' } }}
            >
              Start a watch
            </Button>
          </Stack>
        )}
      </Toolbar>
    </AppBar>
  );
}

export default Header;
